import React, { useEffect, useMemo, useState } from 'react';
import QRCode from 'react-qr-code';
import CountUp from 'react-countup';
import { motion, AnimatePresence } from 'framer-motion';
import { Stack, Box, Typography, Modal, Button, Paper } from '@mui/material';
import useSignalR from '../hooks/useSignalR';
import api from '../services/api';

// ... (importações mantidas iguais)
const EventoPublicInfoPage = () => {
  const [eventoId, setEventoId] = useState(null);
  const [evento, setEvento] = useState(null);
  const [resumo, setResumo] = useState({ totalKids: 0, kidsPresentes: 0, alertasEnviados: 0 });
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [alertas, setAlertas] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [eventosDisponiveis, setEventosDisponiveis] = useState([]);
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const clock = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(clock);
  }, []);

  const carregarEvento = async (mostrarLoading = true) => {
    if (mostrarLoading) setLoading(true);
    try {
      const resPublicos = await api.get('/api/Eventos/publicos-ativos');
      const eventosAtivos = resPublicos.data?.eventos || [];

      if (eventosAtivos.length === 0) {
        console.warn('Nenhum evento ativo encontrado.');
        setLoading(false);
        return;
      }

      if (eventosAtivos.length === 1) {
        selecionarEvento(eventosAtivos[0]);
      } else {
        setEventosDisponiveis(eventosAtivos);
        setOpenModal(true);
      }
    } catch (err) {
      console.error('Erro ao carregar evento:', err);
    } finally {
      if (mostrarLoading) setLoading(false);
    }
  };

  const selecionarEvento = async (eventoSelecionado) => {
    const token = eventoSelecionado.token;
    try {
      const resResumo = await api.get(`/api/Eventos/resumo-token/${token}`);
      setResumo(resResumo.data);
      setEventoId(resResumo.data?.evento?.id || null);
      const resEvento = await api.get(`/api/Eventos/public/${token}`);
      setEvento({ ...resEvento.data, token });
    } catch (err) {
      console.error('Erro ao buscar detalhes do evento:', err);
    } finally {
      setOpenModal(false);
    }
  };

  useEffect(() => {
    carregarEvento(true);
  }, []);

  const signalROptions = useMemo(() => ({
  grupos: [`web_${eventoId}`],
  eventos: ['receberAlerta', 'confirmarCheckout', 'checkoutSolicitado', 'checkinEfetuado', 'reloadKids'],
  onEvent: (event, payload) => {
    if (event === 'receberAlerta') {
      const titulo = payload?.titulo?.toLowerCase?.();

      if (titulo === 'alerta de dependente') {
        setAlertas([{
          id: payload.dependenteId || payload.id || Math.random().toString(),
          mensagem: `Responsável pelo "${payload.nome}" comparecer ao Kids!`
        }]);
      }

      if (titulo === 'limpar alerta') {
        setAlertas([]);
      }
    }

    if (['confirmarCheckout', 'checkoutSolicitado', 'checkinEfetuado', 'reloadKids'].includes(event)) {
      carregarEvento(false);
    }
  }
}), [eventoId]);



  useSignalR('/hub/alertas', signalROptions);

  if (loading) {
    return <div style={styles.loading}>⏳ Carregando evento...</div>;
  }

  return (
    <div style={{ ...styles.page, backgroundImage: `url(${evento?.logoUrl})` }}>
      <div style={styles.mask} />

      <AnimatePresence>
        {alertas.map((a) => (
          <motion.div
            key={a.id}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            style={styles.alertaMensagemCentral}
          >
            <Typography variant="h3" style={{ fontWeight: 900, color: '#000' }}>
              {a.mensagem}
            </Typography>
          </motion.div>
        ))}
      </AnimatePresence>

      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Paper style={styles.modalContent}>
          <Typography variant="h5" gutterBottom>Escolha o evento:</Typography>
          {eventosDisponiveis.map((e) => (
            <Button
              key={e.id}
              variant="outlined"
              fullWidth
              onClick={() => selecionarEvento(e)}
              style={{ marginBottom: 10 }}
            >
              {e.titulo} • {new Date(e.data).toLocaleDateString()} • {e.hora || 'sem hora'}
            </Button>
          ))}
        </Paper>
      </Modal>

      <div style={{
        ...styles.content,
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'center' : 'flex-start',
        gap: isMobile ? '40px' : '80px',
      }}>
        <div style={{ ...styles.leftColumn, alignItems: isMobile ? 'center' : 'flex-start', textAlign: isMobile ? 'center' : 'left' }}>
          <div style={styles.headlineRow}>
            <h1 style={styles.headline}>{evento?.titulo}</h1>
            <div style={styles.datetimeRow}>
              📅 {evento?.data ? new Date(evento.data).toLocaleDateString() : ''} &nbsp;
              ⏰ {currentTime.toLocaleTimeString()}
            </div>
          </div>

          <Stack
            direction="row"
            spacing={6}
            alignItems="center"
            justifyContent="center"
            flexWrap="wrap"
            mt={6}
          >
            <Box textAlign="center">
              <Typography style={styles.counterNumber}><CountUp end={resumo.totalKids} duration={2} /></Typography>
              <Typography variant="h5" sx={{ mt: 1, color: '#fffa65', fontWeight: '600' }}>Total Kids</Typography>
            </Box>
            <Box textAlign="center">
              <Typography style={{ ...styles.counterNumber, color: '#00e676' }}><CountUp end={resumo.kidsPresentes} duration={2} /></Typography>
              <Typography variant="h5" sx={{ mt: 1, color: '#00e676', fontWeight: '600' }}>Presentes</Typography>
            </Box>
            <Box textAlign="center">
              <Typography style={{ ...styles.counterNumber, color: '#ff9800' }}><CountUp end={resumo.alertasEnviados} duration={2} /></Typography>
              <Typography variant="h5" sx={{ mt: 1, color: '#ff9800', fontWeight: '600' }}>Com Alerta</Typography>
            </Box>
          </Stack>
        </div>

        <div style={styles.qrCard}>
          <Typography variant="h5" style={{ color: '#fff', marginBottom: 16, fontWeight: 700 }}>
            Acesse para Checkin
          </Typography>

          <div style={{ padding: '16px', background: '#fff', borderRadius: '12px' }}>
            <QRCode
              value={process.env.REACT_APP_PUBLIC_QRCODE_URL || ''}
              size={isMobile ? 220 : 320}
            />
          </div>

          {evento?.token && (
            <Typography
              variant="h4"
              style={{
                marginTop: 20,
                color: '#00e676',
                fontWeight: 'bold',
                textAlign: 'center',
                letterSpacing: '1px',
              }}
            >
              Código: {evento.token.toUpperCase()}
            </Typography>
          )}

          <p style={styles.instructions}>📲 Aponte a câmera do seu celular para este QR Code</p>
        </div>
      </div>

      <footer style={{ ...styles.footer, position: isMobile ? 'relative' : 'absolute' }}>
        Powered by <strong>Lion of Code</strong> • Tecnologia Premium para Eventos
      </footer>
    </div>
  );
};

export default EventoPublicInfoPage;


const styles = {
  page: {
    height: '100vh',
    overflow: 'hidden',
    fontFamily: 'Arial, sans-serif',
    position: 'relative',
    color: '#fff',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat'
  },
  mask: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    zIndex: 2
  },
  content: {
    position: 'relative',
    zIndex: 3,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    padding: '40px',
  },
  leftColumn: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: '20px',
  },
  headlineRow: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '30px',
    textAlign: 'center',
  },
  headline: {
    fontSize: '32px',
    fontWeight: '900',
    color: '#ffffff',
    textShadow: '3px 3px 10px rgba(0, 0, 0, 0.8)',
    marginBottom: '5px',
  },
  datetimeRow: {
    fontSize: '18px',
    color: '#f0f0f0',
    textShadow: '1px 1px 4px rgba(0, 0, 0, 0.6)',
  },
  qrCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    padding: '20px',
    borderRadius: '16px',
    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.4)',
    backdropFilter: 'blur(10px)',
    textAlign: 'center',
    border: '2px solid #DAA520',
    marginTop: '40px',
  },
  instructions: {
    marginTop: '12px',
    fontSize: '16px',
    color: '#f0f0f0',
  },
  footer: {
    position: 'absolute',
    bottom: '10px',
    width: '100%',
    textAlign: 'center',
    fontSize: '14px',
    color: '#ccc',
    zIndex: 3,
  },
  loading: {
    textAlign: 'center',
    marginTop: '50px',
    fontSize: '20px',
    color: '#fff',
  },
  alertaMensagemCentral: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    background: '#ffeb3b',
    padding: '48px 80px',
    borderRadius: '24px',
    textAlign: 'center',
    boxShadow: '0 0 28px rgba(0,0,0,0.75)',
    zIndex: 25,
    backdropFilter: 'blur(10px)',
    color: '#000'
  },
  counterNumber: {
    fontSize: '124px',
    fontWeight: 'bold',
    color: '#fff',
    textShadow: '2px 2px 12px rgba(0,0,0,0.8)',
    lineHeight: 1,
  },
  modalContent: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: '#fff',
    padding: '32px',
    borderRadius: '16px',
    maxWidth: '500px',
    width: '90%',
  }
};
